import _extends from "@babel/runtime/helpers/extends";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { useEffect, useLayoutEffect, useState } from 'react';
import { DesignContextForwardProvider, useDesignContext } from '@klarna/designtokens';
import { View } from '@bubble-ui/primitive-view';
import { useViewSize } from '@bubble-ui/hook-use-view-size';
import { TouchableControlProvider, useTouchableControl } from '@bubble-ui/provider-touchable-control';
import { usePropDeprecationWarning } from '@bubble-ui/hook-use-prop-deprecation-warning';
import { useTokenDeprecationWarning } from '@bubble-ui/hook-use-token-deprecation-warning';
import { usePropsFromContext } from '@bubble-ui/provider-props-context';
import { SHOW_USAGE_WARNINGS } from '@bubble-dev/build-config';
import { defaultComponentTokens } from './defaultComponentTokens';
import { getDefaultStyleSheet } from './getDefaultStyleSheet';
import { SlotBottom, SlotMiddle, SlotRight, SlotLeft } from './Slots';
import { getDisplayName, mapTypographyElement, hasValidChildren } from './utils';
var ItemInformation = _ref => {
  var {
    borderColorToken,
    borderColorTokenArgs,
    children,
    description,
    disabled,
    framed,
    hasBorder,
    id,
    minHeight = 56,
    horizontalPaddingToken,
    role,
    title,
    verticalPaddingToken = 'item-information/vertical-padding',
    UNSAFE_containerProps,
    UNSAFE_style
  } = _ref;
  usePropDeprecationWarning({
    componentName: 'ItemInformation',
    propName: 'hasBorder',
    shouldWarn: hasBorder !== undefined,
    useInstead: 'borderColorToken="colors/borders/subtle-floating"',
    removalDate: 'v8.0.0'
  });
  var propsFromLayoutContext = usePropsFromContext(ItemInformation.displayName);
  var designContext = useDesignContext(defaultComponentTokens);
  var deprecatedColorTokenExists = designContext.hasTokenInContext('item-information/border-color');
  var isFramedFinal = framed !== undefined ? framed : propsFromLayoutContext.framed !== undefined ? propsFromLayoutContext.framed : designContext.get('item-information/framed');
  useTokenDeprecationWarning({
    componentName: 'ItemInformation',
    tokenName: 'item-information/border-color',
    shouldWarn: deprecatedColorTokenExists,
    removalDate: 'v8.0.0'
  });
  var [showNonSupportedChildWarning, setShowNonSupportedChildWarning] = useState(true);
  var childrenArray = React.Children.toArray(children);
  useEffect(() => {
    if (SHOW_USAGE_WARNINGS && showNonSupportedChildWarning && childrenArray.filter(React.isValidElement).some(child => {
      var _getDisplayName;
      var slotDisplayName = (_getDisplayName = getDisplayName(child)) === null || _getDisplayName === void 0 ? void 0 : _getDisplayName.replace('ItemInformation.', '').replace('ItemTouchable.', '');
      return !['SlotLeft', 'SlotRight', 'SlotMiddle', 'SlotBottom'].includes(slotDisplayName);
    })) {
      console.warn("BUBBLE-UI USAGE WARNING: You are passing a child to 'ItemInformation'/'ItemTouchable' that is not supported and therefor won't be rendered.\nRead more on https://bubble-ui.klarna.net/#components/item-information?tab=proptypes&propName=children");
      setShowNonSupportedChildWarning(false);
    }
  }, [childrenArray, showNonSupportedChildWarning, setShowNonSupportedChildWarning]);
  var slotLeft;
  var slotLeftFullyCentered;
  var slotRight;
  var slotRightFullyCentered;
  var slotMiddle;
  var slotBottom;
  React.Children.map(children, child => {
    if (! /*#__PURE__*/React.isValidElement(child) || !hasValidChildren(child)) {
      return null;
    }
    if (getDisplayName(child).endsWith('.SlotLeft')) {
      if (child.props.position === 'centered') {
        slotLeftFullyCentered = child;
      } else {
        slotLeft = child;
      }
    }
    if (getDisplayName(child).endsWith('.SlotRight')) {
      if (child.props.position === 'centered') {
        slotRightFullyCentered = child;
      } else {
        slotRight = child;
      }
    }
    if (getDisplayName(child).endsWith('.SlotBottom')) {
      slotBottom = child;
    }
    if (getDisplayName(child).endsWith('.SlotMiddle')) {
      slotMiddle = child;
    }
  });
  var [outerMiddleContainerViewProps, {
    height: outerMiddleContainerHeight
  }] = useViewSize();
  var [innerMiddleContainerViewProps, {
    height: innerMiddleContainerHeight
  }] = useViewSize();
  var [slotRightViewProps, {
    height: slotRightHeight
  }] = useViewSize();
  var [slotRightChildrenViewProps, {
    height: slotRightChildrenHeight
  }] = useViewSize();
  var [slotLeftViewProps, {
    height: slotLeftHeight
  }] = useViewSize();
  var [slotLeftChildrenViewProps, {
    height: slotLeftChildrenHeight
  }] = useViewSize();

  // Needed for ItemTouchable
  var {
    turnOffTouchability
  } = useTouchableControl();
  var titlePassed = typeof title === 'string' && title.length > 0 || /*#__PURE__*/React.isValidElement(title);
  var descriptionPassed = typeof description === 'string' && description.length > 0 || /*#__PURE__*/React.isValidElement(description);
  var titleLineHeight = designContext.get('item-information/title-text-style').lineHeight;
  var descriptionLineHeight = designContext.get('item-information/description-text-style').lineHeight;
  var gapBetweenTitleAndDescription = 2;

  // only relevant for position="balanced"
  var slotMinHeight = titlePassed && descriptionPassed ? titleLineHeight + gapBetweenTitleAndDescription + descriptionLineHeight : titlePassed ? titleLineHeight : descriptionPassed ? descriptionLineHeight : 0;

  // padding aligning right/left slot to title if position="title"
  var [topPaddingToTitleSlotLeft, setTopPaddingToTitleSlotLeft] = useState(0);
  var [topPaddingToTitleSlotRight, setTopPaddingToTitleSlotRight] = useState(0);
  var hideSlotLeft = slotLeft && slotLeft.props.hideUntilPositionSyncs && slotLeft.props.position === 'title';
  var hideSlotRight = slotRight && slotRight.props.hideUntilPositionSyncs && slotRight.props.position === 'title';
  var [slotLeftVisible, setSlotLeftVisible] = useState(!hideSlotLeft);
  var [slotRightVisible, setSlotRightVisible] = useState(!hideSlotRight);
  useLayoutEffect(() => {
    var paddingOfMiddleContainer = (outerMiddleContainerHeight - innerMiddleContainerHeight) / 2;
    var titlePositionAxis = paddingOfMiddleContainer + titleLineHeight / 2;
    var newTopPaddingToTitleSlotLeft = Math.max(0, titlePositionAxis - slotLeftChildrenHeight / 2);
    var newTopPaddingToTitleSlotRight = Math.max(0, titlePositionAxis - slotRightChildrenHeight / 2);
    if (slotLeft && slotLeft.props.position === 'title' && slotLeftChildrenHeight > 0 && outerMiddleContainerHeight > 0 && innerMiddleContainerHeight > 0 && topPaddingToTitleSlotLeft !== newTopPaddingToTitleSlotLeft) {
      setTopPaddingToTitleSlotLeft(newTopPaddingToTitleSlotLeft);
    }
    if (slotRight && slotRight.props.position === 'title' && slotRightChildrenHeight > 0 && outerMiddleContainerHeight > 0 && innerMiddleContainerHeight > 0 && topPaddingToTitleSlotRight !== newTopPaddingToTitleSlotRight) {
      setTopPaddingToTitleSlotRight(newTopPaddingToTitleSlotRight);
    }

    // set slotRight visible
    if (!slotRightVisible && slotRight && slotRightHeight > 0 && slotRightChildrenHeight > 0) {
      if (slotRight.props.position === 'title' && slotRightChildrenHeight > 0 && outerMiddleContainerHeight > 0 && innerMiddleContainerHeight > 0 && topPaddingToTitleSlotRight === newTopPaddingToTitleSlotRight) {
        setSlotRightVisible(true);
      }
    }

    // set slotLeft visible
    if (!slotLeftVisible && slotLeft && slotLeftHeight > 0 && slotLeftChildrenHeight > 0) {
      if (slotLeft.props.position === 'title' && slotLeftChildrenHeight > 0 && outerMiddleContainerHeight > 0 && innerMiddleContainerHeight > 0 && topPaddingToTitleSlotLeft === newTopPaddingToTitleSlotLeft) {
        setSlotLeftVisible(true);
      }
    }
  }, [innerMiddleContainerHeight, outerMiddleContainerHeight, slotLeft, slotLeftChildrenHeight, slotLeftHeight, slotMinHeight, slotRight, slotRightChildrenHeight, slotRightHeight, titleLineHeight, topPaddingToTitleSlotLeft, topPaddingToTitleSlotRight, slotRightVisible, slotLeftVisible]);
  var {
    wrapperA,
    wrapperB,
    wrapperC,
    wrapperD,
    wrapperE,
    wrapperF
  } = getDefaultStyleSheet({
    borderColorToken,
    borderColorTokenArgs,
    designContext,
    disabled,
    framed: isFramedFinal,
    hasBorder,
    horizontalPaddingToken: horizontalPaddingToken || propsFromLayoutContext.horizontalPaddingToken || 'item-information/side-padding',
    minHeight,
    UNSAFE_style: _objectSpread(_objectSpread({}, propsFromLayoutContext.UNSAFE_style), UNSAFE_style),
    verticalPaddingToken
  });
  return /*#__PURE__*/React.createElement(View, _extends({}, UNSAFE_containerProps, {
    accessibilityRole: role,
    role: role,
    style: wrapperA,
    id: id
  }), /*#__PURE__*/React.createElement(View, {
    style: wrapperB
  }, /*#__PURE__*/React.createElement(TouchableControlProvider, {
    turnOffTouchability: disabled || turnOffTouchability
  }, /*#__PURE__*/React.isValidElement(slotLeftFullyCentered) && /*#__PURE__*/React.cloneElement(slotLeftFullyCentered, {
    isVisible: true
  }), /*#__PURE__*/React.createElement(View, {
    style: wrapperC
  }, /*#__PURE__*/React.createElement(View, {
    style: wrapperD
  }, /*#__PURE__*/React.isValidElement(slotLeft) && /*#__PURE__*/React.cloneElement(slotLeft, {
    slotViewProps: slotLeftViewProps,
    slotMinHeight,
    topPaddingToTitle: topPaddingToTitleSlotLeft,
    slotChildrenViewProps: slotLeftChildrenViewProps,
    isVisible: slotLeftVisible
  }), /*#__PURE__*/React.createElement(View, _extends({
    style: wrapperE
  }, outerMiddleContainerViewProps), /*#__PURE__*/React.createElement(View, _extends({
    style: wrapperF
  }, innerMiddleContainerViewProps), /*#__PURE__*/React.createElement(DesignContextForwardProvider, {
    context: designContext
  }, mapTypographyElement(title, 'item-information/title-text-style', 'item-information/title-text-color'), mapTypographyElement(description, 'item-information/description-text-style', 'item-information/description-text-color')), /*#__PURE__*/React.isValidElement(slotMiddle) && /*#__PURE__*/React.cloneElement(slotMiddle, {
    shouldHavePaddingTop: titlePassed || descriptionPassed
  }))), /*#__PURE__*/React.isValidElement(slotRight) && /*#__PURE__*/React.cloneElement(slotRight, {
    slotViewProps: slotRightViewProps,
    slotMinHeight,
    topPaddingToTitle: topPaddingToTitleSlotRight,
    slotChildrenViewProps: slotRightChildrenViewProps,
    isVisible: slotRightVisible
  })), /*#__PURE__*/React.isValidElement(slotBottom) && /*#__PURE__*/React.cloneElement(slotBottom)), /*#__PURE__*/React.isValidElement(slotRightFullyCentered) && /*#__PURE__*/React.cloneElement(slotRightFullyCentered, {
    isVisible: true
  }))));
};
ItemInformation.displayName = 'ItemInformation';
ItemInformation.SlotRight = SlotRight;
ItemInformation.SlotLeft = SlotLeft;
ItemInformation.SlotBottom = SlotBottom;
ItemInformation.SlotMiddle = SlotMiddle;
export { ItemInformation };
//# sourceMappingURL=index.js.map